<template>
  <li
    class="
      col-span-1
      flex flex-col
      text-center
      bg-white
      border border-neutral-200
      rounded-lg
      hover:shadow-xl hover:bg-neutral-50
      transition
      duration-250
      relative
    "
  >
    <a
      :href="`${profileUrl}?search_result_uuid=${partner.search_result_uuid}`"
      rel="noopener"
      target="_blank"
      class="flex items-center justify-start cursor-pointer h-full group"
    >
      <div
        v-if="hasRatings"
        class="absolute top-0 left-0 p-1 rounded-lg p-3"
      >
        <dt class="sr-only">{{ $t('search.rating-and-reviews') }}</dt>
        <dd class="flex items-center justify-center text-base tracking-tight">
          <fa
            :icon="['fad', 'star']"
            class="text-xl text-bubblegum mr-1"
          />
          <span class="font-bold mr-1 text-pink-700">
            {{ rating }}
          </span>
          <span class="font-light text-neutral-600">
            ({{ partner.rating_count }})
          </span>
        </dd>
      </div>
      <div class="pt-10 flex flex-col justify-between h-full w-7/12">
        <div class="px-4 flex flex-col items-center justify-start">
          <div
            class="
              w-24
              h-24
              rounded-full
              shrink-0
              bg-neutral-300
              flex
              items-center
              justify-center
              text-5xl text-neutral-400
            "
          >
            <img
              v-if="partner.uri_photo"
              class="w-24 h-24 rounded-full"
              :src="partner.uri_photo"
              :alt="partner.first_name"
            />
            <fa
              v-else
              class="fa-fw"
              :icon="['fal', 'user-md']"
            />
          </div>
          <h3
            class="
              mt-4
              text-neutral-800
              leading-5
              font-medium
              text-lg
              group-hover:text-indigo-600
              transition
            "
          >
            {{ fullName }}
          </h3>
          <dl class="mt-1 grow flex flex-col justify-between items-center">
            <dt class="sr-only">{{ $t('vetster.designation') }}</dt>
            <dd
              class="
                text-neutral-600 text-sm
                leading-5
                group-hover:text-indigo-600
                transition
              "
            >
              {{ fullDesignation }}
            </dd>
            <dd
              v-if="isWellness"
              class="
                text-neutral-600
                mt-2
                text-xs
                leading-5
                group-hover:text-indigo-600
                transition
              "
            >
              {{ $t('search.wellness-coach') }}
            </dd>
            <dt class="sr-only">{{ $t('app.capabilities') }}</dt>
            <div
              v-if="licenseRegion && isVet"
              class="mt-2"
            >
              <ul class="space-y-2 text-xs">
                <li class="flex items-center">
                  <fa
                    :icon="['fal', 'check']"
                    class="text-indigo-500 mr-2 fa-fw"
                  />
                  <span>{{ $t('vetster.licensed-in') }} {{ licenseRegion }}</span>
                </li>
              </ul>
            </div>
            <dd v-if="isWellness">
              <div
                v-tippy="{ zIndex: 0 }"
                content="Vetster Wellness coaches are licensed Veterinary Technicians, but are unable to prescribe medication or diagnose pet health conditions."
                class="
                  mt-4
                  w-8
                  h-8
                  text-pink-100 text-lg
                  leading-4
                  font-medium
                  rounded-full
                  flex
                  items-center
                  justify-center
                  bg-pink-700
                "
                @click.prevent
              >
                <fa
                  class="fa-fw"
                  :icon="['fal', 'spa']"
                />
              </div>
            </dd>
            <dd
              v-else-if="isVet"
              class="mt-5 flex items-center space-x-2"
            >
              <div
                v-tippy="{ zIndex: 0 }"
                :content="
                  canPrescribe
                    ? $t('search.can-prescribe-medications')
                    : $t('search.cannot-prescribe-medications')
                "
                class="
                  w-8
                  h-8
                  text-indigo-100 text-lg
                  leading-4
                  font-medium
                  rounded-full
                  flex
                  items-center
                  justify-center
                "
                :class="
                  canPrescribe
                    ? ['bg-indigo-700', 'text-indigo-100']
                    : ['bg-indigo-100', 'text-indigo-400']
                "
                @click.prevent
              >
                <fa
                  class="fa-fw"
                  :icon="['fal', canPrescribe ? 'prescription' : 'exclamation']"
                />
              </div>
            </dd>
          </dl>
        </div>
        <!-- No prescriptions banner -->
        <div class="mt-8 w-full">
          <div
            v-if="isGeoRestricted && isVet"
            v-tippy="{ zIndex: 0 }"
            class="
              flex
              items-center
              justify-center
              px-2.5
              rounded-bl-lg
              py-1
              text-sm
              leading-5
              bg-yellow-50
              text-yellow-600
              w-full
              border-t border-yellow-100
            "
            :content="`${$t('search.not-able-to-rx')}`"
            @click.prevent
          >
            {{ $t('app.geo-restrictions') }}
            <fa
              :icon="['fal', 'question']"
              class="text-yellow-600 ml-1"
            />
          </div>
        </div>
      </div>
      <div class="flex flex-col justify-between w-5/12 h-full">
        <div class="flex flex-col flex-1 items-end justify-center">
          <div class="flex flex-col p-6">
            <div
              v-if="partner.rate && !hidePricing"
              class="flex flex-col items-end"
            >
              <div
                v-if="isVet"
                class="font-light text-sm leading-normal"
              >
                {{ $t('app.starting-at') }}
              </div>
              <div
                v-if="isWellness"
                class="font-light text-sm leading-normal"
              >
                {{ $t('search.flat-rate') }}
              </div>
              <div
                class="text-4xl font-bold leading-normal -mt-2"
                :class="{
                  'text-indigo-700': isVet,
                  'text-pink-700': isWellness,
                }"
              >
                {{
                  $n(formatMoneyRounded(partner.starting_rate), {
                    style: 'currency',
                    currency: partner.rate_currency,
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                  })
                }}
              </div>
            </div>
            <div
              v-else-if="!hidePricing"
              class="flex flex-col items-end"
            >
              <div class="font-light text-sm leading-normal">
                {{ $t('app.starting-at') }}
              </div>
              <div class="text-3xl font-bold text-indigo-700 leading-normal -mt-2">
                {{ $t('search.click-for-rate') }}
              </div>
            </div>
          </div>
        </div>
        <div class="bg-indigo-50 rounded-br-lg">
          <button
            v-for="(appointment, index) in partner.appointments"
            :key="`${partner.uuid}-${index}`"
            :aria-label="$t('quote.select-timeslot', {
              timeslot: `${getAppointmentDate(appointment.start_at)} ${getAppointmentTime(appointment.start_at)}`,
            })"
            class="
              focus:outline-none focus:bg-indigo-100
              w-full
              border-t border-l border-indigo-200
              flex
              items-center
              justify-between
              hover:bg-indigo-300
              px-3
              py-3
              cursor-pointer
              relative
              transition
              duration-150
            "
            :class="{ 'rounded-br-lg': index === 2 }"
            type="button"
            @click.prevent="redirectToQuote(appointment)"
          >
            <div
              class="items-start w-full"
              :class="{
                'text-left justify-start': isVet,
                'text-center justify-center': isWellness,
              }"
            >
              <div class="text-sm font-normal">
                {{ getAppointmentDate(appointment.start_at) }}
              </div>
              <div class="text-sm font-light text-neutral-600">
                {{ getAppointmentTime(appointment.start_at) }}
              </div>
            </div>
            <div
              v-if="isVet && !hidePricing"
              class="
                flex-1
                items-start
                justify-start
                text-right text-indigo-700
              "
            >
              {{
                $n(formatMoneyRounded(appointment.rate.amount), {
                  style: 'currency',
                  currency: appointment.rate.currency,
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                })
              }}
            </div>
          </button>
        </div>
      </div>
    </a>
  </li>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faQuestion,
  faSlash,
  faSpa,
} from '@fortawesome/pro-light-svg-icons';
import { DateTime } from 'luxon';
import { mapGetters } from 'vuex';

import usePartnerHelpers from '@/composables/use-partner-helpers';
import Designation from '@/enums/Designation';
import {
  formatMoneyRounded,
  formatRating,
} from '@/helpers/format';
import { i18n } from '@/i18n';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';

library.add({
  faQuestion,
  faSlash,
  faSpa,
});

export default {
  name: 'VetsterSearchTile',

  mixins: [LocalizesRoutes],

  // IMPORTANT!
  // NOTE! This is not the same as a User model, it is an Elasticsearch result so user functions do not work
  props: {
    vcprEnabled: {
      required: true,
      type: Boolean,
    },
    timezone: {
      required: true,
      type: String,
    },
    partner: {
      type: Object,
      required: true,
    },
    searchParams: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    const {
      fullDesignation,
      fullName,
    } = usePartnerHelpers(props.partner);

    return {
      fullDesignation,
      fullName,
    };
  },

  computed: {
    ...mapGetters([
      'getSiteLocale',
      'hidePricing',
    ]),

    hasRatings() {
      return this.partner.rating_count > 0;
    },

    licenseRegion() {
      return this.partner.license_guidelines?.license_jurisdiction?.name;
    },

    canPrescribe() {
      return this.vcprEnabled && !this.isGeoRestricted;
    },

    isGeoRestricted() {
      return this.isVet
        && this.getSiteLocale !== 'en-GB'
        && !this.vcprEnabled;
    },

    isVet() {
      return this.partner.designation === Designation.Veterinarian;
    },

    isWellness() {
      return this.partner.designation === Designation.Technician;
    },

    profileUrl() {
      return this.localizeUrl(`p/${this.partner.uri_slug}`);
    },

    rating() {
      return formatRating(this.partner);
    },
  },

  methods: {
    buildTimeslotUrl(appointment) {
      const date = DateTime.fromISO(appointment.start_at, {
        zone: this.timezone,
      }).toISODate();

      const params = new URLSearchParams({
        date,
        search_result_uuid: this.partner.search_result_uuid,
        timeslot: appointment.start_time,
        timezone: this.timezone,
      });

      return `${this.profileUrl}?${params.toString()}`;
    },

    formatMoneyRounded,

    getAppointmentDate(timestamp) {
      return DateTime.fromISO(timestamp, {
        zone: this.timezone,
      })
        .setLocale(i18n.locale)
        .toLocaleString(DateTime.DATE_MED);
    },

    getAppointmentTime(timestamp) {
      return DateTime.fromISO(timestamp, {
        zone: this.timezone,
      })
        .setLocale(i18n.locale)
        .toLocaleString(DateTime.TIME_SIMPLE);
    },

    redirectToQuote(appointment) {
      window.open(this.buildTimeslotUrl(appointment), '_blank');
    },
  },
};
</script>
